import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string, mixed } from 'yup';

import { formatBytes, sentenceCase } from '../../../utils/utils';
import ActionButton from '../../elements/action-button/ActionButton';
import Title from '../../elements/title/Title';
import ImageUpload from '../../sections/image-upload/ImageUpload';
import { FILE_SIZE, SUPPORTED_FORMATS } from '../instance-stepper/constants';

const ProjectForm = ({ onSubmit, data, handleRemoveImage }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const schema = object({
    name: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('projectName', { ns: 'fields' }),
          })
        )
      )
      .min(2, t('minLength', { ns: 'validations', min: '2' }))
      .max(25, t('maxLength', { ns: 'validations', max: '25' }))
      .matches(
        /^[ぁ-んァ-ン一-龯ー0-9A-Za-z_-]+$/,
        t('nameFormat', { ns: 'validations' })
      ),
    description: string().max(
      256,
      t('maxLength', { ns: 'validations', max: '256' })
    ),
    icon: mixed()
      .test(
        'fileFormat',
        t('invalidImageFormat', { ns: 'validations' }),
        (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))
      )
      .test(
        'fileSize',
        t('invalidSize', {
          ns: 'validations',
          size: formatBytes(FILE_SIZE.BYTES, 0),
        }),
        (value) => !value || (value && value.size <= FILE_SIZE.BYTES)
      ),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const goBack = () => navigate(-1);

  return (
    <Box>
      {data && (
        <Title subTitle text={t('editProject', { ns: 'sectionTitles' })} />
      )}
      <form autoComplete="off">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, my: 4 }}>
          <ImageUpload
            name="icon"
            control={control}
            limit={FILE_SIZE.BYTES}
            imageSrc={data?.icon}
            onRemoveImage={handleRemoveImage}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="name"
              control={control}
              defaultValue={data ? data.name : ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('projectName', { ns: 'fields' })}
                  error={!!errors?.name}
                  helperText={errors?.name?.message}
                  inputProps={{ 'aria-label': 'name' }}
                  size="small"
                  sx={{ width: 600 }}
                />
              )}
            />
            <TextField
              {...register('description')}
              label={t('projectDesc', { ns: 'fields' })}
              defaultValue={data ? data?.description : ''}
              error={!!errors?.description}
              helperText={errors?.description?.message}
              inputProps={{
                'aria-label': 'description',
                maxLength: 256,
              }}
              sx={{ width: 600 }}
              size="small"
            />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {data ? (
                <ActionButton
                  disabled={!isDirty || !isValid}
                  aria-label="update"
                  clickAction={handleSubmit(onSubmit)}
                >
                  {t('save', { ns: 'buttons' })}
                </ActionButton>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={goBack}
                    aria-label="go back"
                    style={{ marginRight: 10 }}
                  >
                    {t('back', { ns: 'buttons' })}
                  </Button>
                  <ActionButton
                    aria-label="create"
                    disabled={!isValid}
                    clickAction={handleSubmit(onSubmit)}
                  >
                    {t('create', { ns: 'buttons' })}
                  </ActionButton>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

ProjectForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  data: PropTypes.object,
  handleRemoveImage: PropTypes.func,
};

export default ProjectForm;
